import React from 'react';
import { motion } from 'framer-motion';
import { 
  Bell,
  Calendar,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Info,
  Settings,
  Trash2,
  Filter
} from 'lucide-react';
import { useAdminNotifications } from '../../hooks/useAdminNotifications';
import type { NotificationType } from '../../types/notifications';

const NotificationIcon = ({ type }: { type: NotificationType }) => {
  switch (type) {
    case 'success':
      return <CheckCircle2 className="w-5 h-5 text-green-500" />;
    case 'error':
      return <XCircle className="w-5 h-5 text-red-500" />;
    case 'warning':
      return <AlertCircle className="w-5 h-5 text-yellow-500" />;
    default:
      return <Info className="w-5 h-5 text-blue-500" />;
  }
};

const Notifications = () => {
  const {
    notifications,
    loading,
    filter,
    setFilter,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications
  } = useAdminNotifications();

  if (loading) {
    return (
      <div className="min-h-screen pt-28 pb-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-center h-64">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Bell className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold">Notificações</h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={markAllAsRead}
              className="text-sm text-primary hover:text-primary/80 transition-colors"
            >
              Marcar todas como lidas
            </button>
            <button
              onClick={deleteAllNotifications}
              className="text-sm text-destructive hover:text-destructive/80 transition-colors"
            >
              Limpar todas
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-2 bg-secondary rounded-lg p-1">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === 'all' ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary/80'
              }`}
            >
              Todas
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === 'unread' ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary/80'
              }`}
            >
              Não lidas
            </button>
          </div>
          <button className="p-2 hover:bg-secondary rounded-lg transition-colors">
            <Settings className="w-5 h-5" />
          </button>
          <button className="p-2 hover:bg-secondary rounded-lg transition-colors">
            <Filter className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {notifications.map((notification) => (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`bg-card p-4 rounded-lg ${
                !notification.read ? 'ring-1 ring-primary/50' : ''
              }`}
            >
              <div className="flex items-start gap-4">
                <NotificationIcon type={notification.type} />
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-medium">{notification.title}</h3>
                      <p className="text-sm text-gray-400 mt-1">{notification.message}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {!notification.read && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="p-2 hover:bg-secondary rounded-lg transition-colors"
                          title="Marcar como lida"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notification.id)}
                        className="p-2 hover:bg-secondary rounded-lg transition-colors text-destructive"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-sm text-gray-400">
                    <Calendar className="w-4 h-4" />
                    {new Date(notification.created_at).toLocaleString('pt-PT')}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}

          {notifications.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhuma notificação encontrada</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;